import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'Utils/api';
import * as S from './HeroSlider.style';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

interface HeroSlideData {
  id: string;
  imageUrl: string;
  title: Record<string, string>;
  subtitle: Record<string, string>;
  ctaLabel: Record<string, string>;
  ctaUrl: string | null;
  sortOrder: number;
}

const resolveImage = (url: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url}`;
};

const HeroSlider: React.FC = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const lang = (i18n.language || 'de').slice(0, 2);
  const [slides, setSlides] = useState<HeroSlideData[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/api/hero-slides`);
        if (Array.isArray(res.data?.heroSlides) && res.data.heroSlides.length > 0) {
          setSlides(res.data.heroSlides);
        }
      } catch {
        // silent — section just stays hidden
      }
    })();
  }, []);

  const startAutoSlide = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);
  };

  useEffect(() => {
    startAutoSlide();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  const handleManualSlideChange = (index: number) => {
    setCurrentSlide(index);
    startAutoSlide();
  };

  useEffect(() => {
    const handleScroll = () => setParallaxOffset(window.pageYOffset);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (slides.length === 0) {
    return null;
  }

  const handleCta = (slide: HeroSlideData) => {
    const target = slide.ctaUrl || '/kaufen';
    if (/^https?:\/\//i.test(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      navigate(target);
    }
  };

  return (
    <S.HeroContainer>
      {slides.map((slide, index) => (
        <S.SlideContainer key={slide.id} $active={index === currentSlide}>
          <S.SlideContent $active={index === currentSlide}>
            <S.SlideTitle>{slide.title?.[lang] || slide.title?.de || ''}</S.SlideTitle>
            <S.SlideSubtitle>{slide.subtitle?.[lang] || slide.subtitle?.de || ''}</S.SlideSubtitle>
            <S.SlideButton onClick={() => handleCta(slide)}>
              {slide.ctaLabel?.[lang] || slide.ctaLabel?.de || t('hero.cta')}
            </S.SlideButton>
          </S.SlideContent>
          <S.SlideImage $image={resolveImage(slide.imageUrl)} $parallax={parallaxOffset} />
        </S.SlideContainer>
      ))}

      <S.DotsContainer>
        {slides.map((_, index) => (
          <S.Dot
            key={index}
            $active={index === currentSlide}
            onClick={() => handleManualSlideChange(index)}
          />
        ))}
      </S.DotsContainer>
    </S.HeroContainer>
  );
};

export default HeroSlider;
